import React, { useCallback, useEffect, useState } from "react";
import {
 getEmailLogs,
} from "../../../server/server";
import { notif } from "../../utils/util";
import EmailLogDetailModal from "./EmailLogDetailModal";
import DateSearchHeader from "../../../common/DateSearchHeader";

const STATUS_OPTIONS = [
  { value: "", label: "Сви" },
  { value: "success", label: "Успешно" },
  { value: "failed", label: "Неуспешно" },
];

const PURPOSE_OPTIONS = [
  { value: "", label: "Сви" },
  { value: "registration", label: "Регистрација" },
  { value: "password_reset", label: "Ресет лозинке" },
  { value: "contact", label: "Контакт" },
  { value: "chat_notifications", label: "Обавештења ћаскања" },
  { value: "fallback", label: "Резервно" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sr-Latn-RS", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const purposeLabel = (p: string) => {
  const found = PURPOSE_OPTIONS.find((o) => o.value === p);
  return found ? found.label : p;
};

interface EmailLogRow {
  id: string;
  to: string;
  from: string;
  subject: string;
  status: string;
  purpose: string;
  sentAt: string;
  sentBy: number | null;
  resendFromId: string | null;
  errorMessage: string | null;
}

interface EmailLogsResponse {
  data: EmailLogRow[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const EmailLogs: React.FC = () => {
  const [logs, setLogs] = useState<EmailLogRow[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Detail modal
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const fetchLogs = useCallback(
    async (page = 1) => {
      setLoading(true);
      const resp = await getEmailLogs({
        page,
        limit: meta.limit,
        status: statusFilter || undefined,
        search: searchFilter || undefined,
        purpose: purposeFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (resp.status === 200) {
        const body = resp.data as EmailLogsResponse;
        setLogs(body.data);
        setMeta(body.meta);
      } else {
        notif("Грешка при учитавању е-поште", "error");
      }
      setLoading(false);
    },
    [meta.limit, statusFilter, searchFilter, purposeFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, purposeFilter, searchFilter, dateFrom, dateTo, meta.limit]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > meta.totalPages) return;
    fetchLogs(newPage);
  };

  return (
    <div className="py-10 px-6 overflow-auto gray-small-scroll h-full w-full">
      <h1 className="text-[#04438b] text-4xl font-bold mb-8">Евиденција е-поште</h1>

      {/* ── Filters ─────────────────────────────────────── */}
      <div className="rounded-lg border border-[#81848f3d] p-6 mb-6 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Претрага
            </label>
            <input
              type="text"
              placeholder="Прималац / Наслов…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full border border-[#81848f3d] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#04438b]"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Статус
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-[#81848f3d] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#04438b]"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Сврха
            </label>
            <select
              value={purposeFilter}
              onChange={(e) => setPurposeFilter(e.target.value)}
              className="w-full border border-[#81848f3d] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#04438b]"
            >
              {PURPOSE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Датум од
            </label>
            <DateSearchHeader title="" value={dateFrom} onChange={setDateFrom} />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Датум до
            </label>
            <DateSearchHeader title="" value={dateTo} onChange={setDateTo} />
          </div>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────── */}
      <div className="rounded-lg border border-[#81848f3d] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#04438b] text-white text-left">
                <th className="px-4 py-3 font-semibold">Прималац</th>
                <th className="px-4 py-3 font-semibold">Наслов</th>
                <th className="px-4 py-3 font-semibold">Статус</th>
                <th className="px-4 py-3 font-semibold">Сврха</th>
                <th className="px-4 py-3 font-semibold">Послато</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-500">
                    Учитавање…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-500">
                    Нема пронађених записа.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b border-[#81848f3d] hover:bg-[#04438b0a] cursor-pointer transition-colors"
                    onClick={() => setSelectedLogId(log.id)}
                  >
                    <td className="px-4 py-3">{log.to}</td>
                    <td className="px-4 py-3 max-w-[250px] truncate" title={log.subject}>
                      {log.subject}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          log.status === "success"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {log.status === "success" ? "Успешно" : "Неуспешно"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{purposeLabel(log.purpose)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(log.sentAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 border-t border-[#81848f3d] bg-[#f9fafb]">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Прикажи</span>
            <select
              value={meta.limit}
              onChange={(e) => {
                setMeta((prev) => ({ ...prev, limit: Number(e.target.value) }));
              }}
              className="border border-[#81848f3d] rounded-md px-2 py-1 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span>од {meta.total} резултата</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={meta.page <= 1}
              className="px-2 py-1 rounded text-sm hover:bg-[#04438b0a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &#171;
            </button>
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="px-2 py-1 rounded text-sm hover:bg-[#04438b0a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &#8249;
            </button>

            <span className="px-3 py-1 text-sm font-medium text-[#04438b]">
              {meta.page} / {meta.totalPages || 1}
            </span>

            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="px-2 py-1 rounded text-sm hover:bg-[#04438b0a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &#8250;
            </button>
            <button
              onClick={() => handlePageChange(meta.totalPages)}
              disabled={meta.page >= meta.totalPages}
              className="px-2 py-1 rounded text-sm hover:bg-[#04438b0a] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &#187;
            </button>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ────────────────────────────── */}
      {selectedLogId && (
        <EmailLogDetailModal
          logId={selectedLogId}
          isOpen={!!selectedLogId}
          onClose={() => {
            setSelectedLogId(null);
            fetchLogs(meta.page);
          }}
        />
      )}
    </div>
  );
};

export default EmailLogs;
